/**
 * `useSSOLoginFromParent`: hydrates a Better-Auth session from an SSO token
 * forwarded by an enclosing iframe or native shell.
 *
 * Listens for two channels: `postMessage` from a parent window with
 * `type: 'psi-sso-parent-login'` and native login events via
 * `subscribeToContainerLogin`. On either, it calls `authClient.signIn.social`
 * with the supplied `idToken` and provider, then refetches the session so React
 * updates. When the token arrives in response to an explicit `requestAppLogin`
 * call, `requestSignUp` is set and the sign-in attempt also creates the account
 * if the user is new. The hook also signals readiness back to the host
 * (`appReady` on native, an `'psi-app-ready'` postMessage on iframe) so the
 * parent knows when to push the token. Mount once near the root; extra
 * instances will trip listeners twice.
 */

import { getIsInPsiNativeAppWebview } from 'platform-specific/url';
import { useEffect } from 'react';
import { authClient, useSession } from 'util/auth-client';
import { fireAndForget } from 'util/hooks';
import { captureException } from 'util/sentry';
import { getIsInAndroid, getIsInIFrame, getIsInIOS } from 'util/util';

import type { NativeWindow } from './webview';
import { subscribeToContainerLogin } from './webview';

export function useSSOLoginFromParent(): void {
    const { refetch } = useSession();

    useEffect(() => {
        const hydrateSessionTokenFn = async (token: string, provider: string, requestSignUp = false): Promise<void> => {
            const { error } = await authClient.signIn.social({
                provider,
                idToken: { token },
                requestSignUp,
            });

            if (error) {
                if (error.code === 'OAUTH_LINK_ERROR' && !requestSignUp) {
                    // User does not exist or is not linked — expected on the
                    // implicit on-load flow until the user signs in explicitly.
                    // Unfortunately, Better Auth exports no constant for this.
                    return;
                }
                captureException(new Error(`SSO login failed: [${error.status}] ${error.message}`));
            } else {
                await refetch();
            }
        };

        const iframeMessageHandler = (event: MessageEvent) => {
            if (event?.data?.type === 'psi-sso-parent-login') {
                const { ssoToken, provider, requestSignUp } = event?.data ?? {};
                fireAndForget(hydrateSessionTokenFn(ssoToken, provider, requestSignUp));
            }
        };

        const unsubscribeNative = subscribeToContainerLogin(({ ssoToken, provider, requestSignUp }) => {
            fireAndForget(hydrateSessionTokenFn(ssoToken, provider, requestSignUp));
        });

        window.addEventListener('message', iframeMessageHandler);

        const nativeWindow = window as NativeWindow;

        if (getIsInPsiNativeAppWebview()) {
            if (getIsInAndroid(nativeWindow)) {
                nativeWindow.Android?.appReady();
            } else if (getIsInIOS(nativeWindow)) {
                nativeWindow.webkit?.messageHandlers?.appReady?.postMessage('');
            }
        } else if (getIsInIFrame(nativeWindow)) {
            window.parent.postMessage('psi-app-ready', '*');
        }

        return () => {
            unsubscribeNative();
            window.removeEventListener('message', iframeMessageHandler);
        };
    }, [refetch]);
}
